import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { CiteThis } from "@/components/site/CiteThis";
import { GBP_CATEGORIES, listIndustries } from "@/lib/reference/categories";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/tools/gbp-categories/")({
  head: () => {
    const url = `${SITE.url}/tools/gbp-categories`;
    return {
      meta: [
        { title: `GBP Category Finder — Google Business Profile Categories | ${SITE.name}` },
        {
          name: "description",
          content:
            "Searchable index of Google Business Profile categories. See what each unlocks, when to choose it as primary, and the related options operators most often confuse it with.",
        },
        { property: "og:title", content: `GBP Category Finder | ${SITE.name}` },
        { property: "og:description", content: "Search GBP categories and see what attributes each unlocks." },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Google Business Profile categories",
            url,
            numberOfItems: GBP_CATEGORIES.length,
            itemListElement: GBP_CATEGORIES.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: c.name,
              url: `${url}/${c.slug}`,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE.url}/tools` },
              { "@type": "ListItem", position: 3, name: "GBP Categories", item: url },
            ],
          }),
        },
      ],
    };
  },
  component: CategoryIndex,
});

function CategoryIndex() {
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState<string>("All");
  const industries = useMemo(() => ["All", ...listIndustries()], []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return GBP_CATEGORIES.filter((c) => {
      if (industry !== "All" && c.industry !== industry) return false;
      if (!needle) return true;
      return (
        c.name.toLowerCase().includes(needle) ||
        c.slug.includes(needle) ||
        c.synonyms.some((s) => s.toLowerCase().includes(needle))
      );
    });
  }, [q, industry]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-5xl px-5 sm:px-8 py-16">
        <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
          <ol className="flex items-center gap-2 flex-wrap">
            <li><Link to="/" className="hover:text-accent">Home</Link></li>
            <li aria-hidden>/</li>
            <li><Link to="/tools" className="hover:text-accent">Tools</Link></li>
            <li aria-hidden>/</li>
            <li>GBP Categories</li>
          </ol>
        </nav>
        <header className="mt-6 max-w-3xl">
          <p className="eyebrow">Reference</p>
          <h1 className="headline-display mt-3 text-5xl">GBP Category Finder</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Pick the right primary category and you set the ceiling for every ranking decision that follows. Search by name or synonym, filter by industry, and see what attributes each category unlocks.
          </p>
        </header>

        <div className="mt-8 flex items-center gap-3 border-t border-b border-border py-4">
          <CiteThis title="GBP Category Finder" url={`${SITE.url}/tools/gbp-categories`} />
          <span className="text-xs text-muted-foreground">Free reference — please link back.</span>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search categories or synonyms…"
            className="flex-1 min-w-[220px] rounded-sm border border-border bg-paper-soft px-4 py-3 text-sm"
            aria-label="Search categories"
          />
          <select
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="rounded-sm border border-border bg-paper-soft px-4 py-3 text-sm"
            aria-label="Filter by industry"
          >
            {industries.map((i) => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>
        </div>

        <p className="mt-4 text-xs uppercase tracking-wider text-muted-foreground">
          {filtered.length} of {GBP_CATEGORIES.length} categories
        </p>

        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {filtered.map((c) => (
            <li key={c.slug}>
              <Link
                to="/tools/gbp-categories/$slug"
                params={{ slug: c.slug }}
                className="block rounded-sm border border-border bg-paper-soft p-4 hover:border-foreground hover:bg-background transition-colors"
              >
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{c.industry}</p>
                <h2 className="mt-1 text-lg font-semibold">{c.name}</h2>
                <p className="mt-1 text-sm text-foreground/75 line-clamp-2">{c.description}</p>
              </Link>
            </li>
          ))}
        </ul>
      </main>
      <SiteFooter />
    </div>
  );
}
